import type { MouseEvent } from 'react';
import { Eye } from 'lucide-react';
import { Button, useToast } from '@/shared/ui';
import { useRevealDob } from '../hooks/useVerificationActions';
import { formatDob, getErrorMessage } from '../utils/format';

interface RevealDobProps {
  verificationId: string;
  masked: string | null;
}

/**
 * A patient's DOB, masked ("••/••/1984") until the user asks for it. Each
 * reveal is a server call that lands on the audit trail; the value lives only
 * in this component's mutation state.
 */
export function RevealDob({ verificationId, masked }: RevealDobProps) {
  const reveal = useRevealDob(verificationId);
  const toast = useToast();

  if (!masked) return <>—</>;
  if (reveal.data) return <>{formatDob(reveal.data.dateOfBirth)}</>;

  function handleClick(e: MouseEvent) {
    // Sits inside clickable worklist rows — don't open the verification.
    e.stopPropagation();
    reveal.mutate(undefined, {
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't show the date of birth")),
    });
  }

  return (
    <>
      {masked}{' '}
      <Button
        variant="ghost"
        size="sm"
        aria-label="Show date of birth"
        title="Show date of birth"
        loading={reveal.isPending}
        leftIcon={<Eye size={12} aria-hidden />}
        onClick={handleClick}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </>
  );
}
